import {
  ApiError,
  CancelablePromise,
  DashboardMediaService,
  type MediaAttributes,
  type MediaAttributesForm,
  type VaultCategory,
  type VaultMediaItemDto,
  type VaultMediaResponseDto,
  VaultMediaType,
  VaultService,
} from '@api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'

import { updateFileToAdd } from '@/dialogs/AddMediaDialog/models.ts'
import { $creatorId } from '@/models/creators.ts'
import { toast } from '@/models/toasts.ts'
import {
  $selectedCategory,
  renameVaultCategoryFx,
  setSelectedCategory,
} from '@/models/vault/categories.ts'
import {
  $mediaQueryParam,
  $mediaSort,
  $mediaSortField,
  type MediaSortField,
} from '@/models/vault/medias/options.ts'
import { closeVault } from '@/models/vault/vault.ts'
import { chatsService } from '@/modules/chatting/service/chats.ts'
import { MAX_MEDIA_SELECTION_LIMIT } from '@/modules/vault/consts.ts'
import type {
  StreamableVaultMedia,
  UploadFileItem,
} from '@/modules/vault/types.ts'
import { handleError } from '@/utils/error'
import { delay } from '@/utils/misc.ts'

export const resetSelectedMedia = createEvent<unknown>('reset selected medias')
export const toggleSelectedMedia = createEvent<VaultMediaItemDto>(
  'toggle selected medias',
)
export const setSelectedMediaIds = createEvent<number[]>('select media ids')

export const $selectedMediaIds = createStore<number[]>([], {
  name: 'selected media ids',
})
  .on(setSelectedMediaIds, (_, ids) => ids)
  .on(toggleSelectedMedia, (store, { id }) =>
    store.includes(id)
      ? store.filter((o) => o !== id)
      : store.length >= MAX_MEDIA_SELECTION_LIMIT
        ? store
        : [...store, id],
  )
  .reset(resetSelectedMedia)

export const mediaMoveInFx = attach({
  name: 'move media in category',
  source: {
    creatorId: $creatorId,
    mediaIds: $selectedMediaIds,
  },
  mapParams: (categoryId: VaultCategory['id'], { creatorId, mediaIds }) => ({
    creatorId: creatorId!,
    requestBody: {
      categoryId,
      mediaIds,
    },
  }),
  effect: createEffect(VaultService.moveMedias),
})

mediaMoveInFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while adding medias to category')
})

export const mediaMoveOutFx = attach({
  name: 'move media out category',
  source: {
    creatorId: $creatorId,
    mediaIds: $selectedMediaIds,
  },
  mapParams: (categoryId: VaultCategory['id'], { creatorId, mediaIds }) => ({
    categoryId,
    creatorId: creatorId!,
    mediaIds,
  }),
  effect: createEffect(VaultService.deleteMedias),
})

mediaMoveOutFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while removing medias from category')
})

export const deleteMediasFx = attach({
  name: 'delete media from category',
  source: {
    creatorId: $creatorId,
    mediaIds: $selectedMediaIds,
  },
  mapParams: (_: void, { creatorId, mediaIds }) => ({
    creatorId: creatorId!,
    requestBody: { mediaIds },
  }),
  effect: createEffect(VaultService.deleteMedia),
})

deleteMediasFx.doneData.watch(() => {
  toast.success('Selected media items have been deleted successfully')
})

deleteMediasFx.failData.watch((error) => {
  handleError(
    error,
    'Unexpected error occurred while deleting media items, please try again later',
  )
})

sample({
  clock: deleteMediasFx.done,
  target: resetSelectedMedia,
})

export const setMediaCategoryMap = createEvent<Record<number, number[]>>(
  'set media category map',
)
export const $mediaToCategoryMap = createStore<Record<number, number[]>>({}).on(
  setMediaCategoryMap,
  (_, value) => value,
)

export const $mediaHasChanged = createStore(false, {
  name: 'media has changed',
}).on([mediaMoveInFx.doneData, mediaMoveOutFx.doneData], () => true)

export const updateMediaSettingsFx = attach({
  name: 'update media settings',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (params: MediaAttributesForm, { creatorId }) => ({
    creatorId: creatorId!,
    requestBody: params,
  }),
  effect: createEffect(DashboardMediaService.saveMediaAttributes),
})

updateMediaSettingsFx.done.watch(() => {
  toast.success('Media settings has been saved successfully')
})

updateMediaSettingsFx.failData.watch((error) => {
  handleError(
    error,
    'Unexpected error occurred while updating media, please try again later',
  )
})

export const loadSingleMediaFx = attach({
  name: 'load single media item',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (mediaId: number, { creatorId }) => ({
    mediaId,
    creatorId: creatorId!,
  }),
  effect: createEffect<
    {
      mediaId: number
      creatorId: number
    },
    VaultMediaItemDto
  >(({ mediaId, creatorId }) => {
    return VaultService.retrieveMedia({
      mediaId,
      creatorId,
    }) as unknown as Promise<VaultMediaItemDto>
  }),
})

export const $loadingSingleMedia = createStore<number | null>(null)
  .on(loadSingleMediaFx, (_, mediaId) => mediaId)
  .reset(loadSingleMediaFx.doneData, loadSingleMediaFx.failData)

loadSingleMediaFx.failData.watch((error) => {
  handleError(error, 'Failed to load media item')
})

export const loadMultipleMediasFx = attach({
  name: 'load multiple media items',
  mapParams: (mediaIds: number[]) => ({
    mediaIds,
  }),
  effect: createEffect<
    {
      mediaIds: number[]
    },
    void
  >(async ({ mediaIds }) => {
    // TODO: PROPOSAL: use a single query to get all media items in the future if
    // total media items are more than 10
    for await (const mediaId of mediaIds) {
      try {
        await loadSingleMediaFx(mediaId)
      } catch (error) {
        /* empty */
      }
      // we need to avoid reaching the OF requests limit
      await delay(200)
    }
  }),
})

export const mediaSearchFx = attach({
  name: 'search for media',
  source: {
    creatorId: $creatorId,
    text: $mediaQueryParam,
  },
  mapParams: (_: void, { creatorId, text }) => ({
    creatorId: creatorId!,
    text,
  }),
  effect: createEffect<
    { creatorId: number; text: string },
    VaultMediaItemDto[],
    ApiError
  >(async ({ creatorId, text }) => {
    const { medias, missingMediaIds } = await VaultService.search({
      creatorId,
      requestBody: {
        text,
      },
    })

    if (missingMediaIds?.length) {
      const allItems = await Promise.all(
        missingMediaIds.map((mediaId) =>
          VaultService.retrieveMedia({ creatorId, mediaId }),
        ),
      )

      return medias?.concat(allItems.flat()) as VaultMediaItemDto[]
    }

    return medias as VaultMediaItemDto[]
  }),
})

mediaSearchFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while searching media')
})

export const $searchedVaultMedias = createStore<VaultMediaItemDto[]>([])
  .on(mediaSearchFx.doneData, (_, values) => values)
  .reset(mediaSearchFx)

export const setSelectedMediaType = createEvent<VaultMediaType>(
  'set vault media type',
)
export const $selectedMediaType = createStore<VaultMediaType>(
  VaultMediaType.ALL,
  {
    name: 'vault selected media type',
  },
)
  .on(setSelectedMediaType, (_, type) => type)
  .reset(setSelectedCategory, renameVaultCategoryFx.doneData)

let prevRequest: Nullable<CancelablePromise<any>> = null

export const getVaultMediasFx = attach({
  name: 'get vault medias by page',
  source: {
    creatorId: $creatorId,
    sort: $mediaSort,
    type: $selectedMediaType,
    selectedCategory: $selectedCategory,
    field: $mediaSortField,
  },
  mapParams: (
    offset: number,
    { creatorId, sort, type, selectedCategory, field },
  ) => ({
    offset,
    creatorId: creatorId!,
    sort,
    type,
    selectedCategory: selectedCategory?.id,
    field,
  }),
  effect: createEffect<
    {
      creatorId: number
      offset: number
      sort: string
      type: VaultMediaType
      selectedCategory?: number
      field: MediaSortField
    },
    VaultMediaResponseDto,
    ApiError
  >(async ({ offset, creatorId, sort, type, selectedCategory, field }) => {
    if (prevRequest) {
      prevRequest.cancel()
    }
    prevRequest = VaultService.getVaultMedias({
      creatorId,
      sort,
      offset,
      // seems like previously we have an error here, need to test it
      type: type === VaultMediaType.ALL ? undefined : type,
      list: selectedCategory?.toString() ?? VaultMediaType.ALL,
      limit: 24,
      field,
    })

    return prevRequest
  }),
})

getVaultMediasFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while getting medias')
})

export const setRefetchAvailable = createEvent<boolean>(
  'set vault medias refetch available',
)
export const $isRefetchAvailable = createStore(true, {
  name: 'is vault medias refetch available',
})
  .on(setRefetchAvailable, (_, value) => value)
  .on(getVaultMediasFx, () => false)

getVaultMediasFx.doneData.watch(() => {
  // allow to refetch medias after 5 seconds
  setTimeout(setRefetchAvailable, 5000, true)
})

export const refetchVaultMedias = createEvent<unknown>('refetch vault medias')
export const $vaultMediasPages = createStore<VaultMediaResponseDto[]>([])
  .on(getVaultMediasFx.doneData, (state, data) => [...state, data])
  .reset(refetchVaultMedias)

export const $vaultMediasError = createStore<Nullable<ApiError>>(null)
  .on(getVaultMediasFx.failData, (_, error) =>
    error.name !== 'CancelError' ? error : null,
  )
  .on(mediaSearchFx.failData, (_, error) => error)
  .reset(closeVault, getVaultMediasFx, mediaSearchFx)

export const $canLoadMoreVaultMediaPages = combine(
  $vaultMediasPages,
  getVaultMediasFx.pending,
  (pages, isLoading) =>
    (pages.length ? pages[pages.length - 1].hasMore : true) && !isLoading,
)

export const addMediaFx = attach({
  name: 'add media',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (
    item: UploadFileItem & { selectedCategoryId?: number },
    { creatorId },
  ) => ({
    ...item,
    creatorId: creatorId!,
    file: item.file,
    fileName: item.file.name,
    isVaultMedia: true,
    onUploadProgress: ({
      total,
      loaded,
    }: {
      loaded: number
      total: number
    }) => {
      const uploadingProgress = Math.round((loaded * 100) / (total || 100))

      updateFileToAdd({
        ...item,
        uploadingProgress,
        isUploading: true,
      })
    },
  }),
  effect: createEffect(chatsService.uploadMediaInChunks),
})

addMediaFx.fail.watch(({ params: { selectedCategoryId, ...item }, error }) => {
  updateFileToAdd({
    ...item,
    isUploading: false,
  })
  handleError(error, `Could not upload the file: ${item.file.name}`)
})

export const getNextVaultMediasPageFx = attach({
  name: 'get vault medias next page',
  source: { pages: $vaultMediasPages },
  mapParams: (_: void, { pages }) => pages,
  effect: createEffect({
    handler: async (pages: VaultMediaResponseDto[]) => {
      const offset = pages.reduce((result, { list }) => result + list.length, 0)

      return getVaultMediasFx(offset)
    },
  }),
})

export const $vaultMedias = createStore<VaultMediaItemDto[]>([])
  .on(getVaultMediasFx.doneData, (state, { list }) => {
    return state.concat(list)
  })
  .on(updateMediaSettingsFx.doneData, (state, media) =>
    state.map((item) =>
      item.id === media.id
        ? {
            ...item,
            mediaAttributes: media.mediaAttributes as Required<MediaAttributes>,
          }
        : item,
    ),
  )
  .reset(refetchVaultMedias)

export const updateMediaDataMap = createEvent<StreamableVaultMedia[]>(
  'update media data map',
)

export const $vaultMediasMap = createStore<
  Record<number, StreamableVaultMedia>
>({})
  .on(updateMediaDataMap, (store, data) =>
    data.reduce(
      (result, value) => {
        const prevValue = result[value.id]

        if (
          (!!prevValue?.files.thumb?.url && !value.files.thumb?.url) ||
          (!!prevValue?.files.full?.url && !value.files.full?.url)
        ) {
          result[value.id] = {
            ...value,
            files: prevValue.files,
          }
        } else {
          result[value.id] = value
        }

        return result
      },
      { ...store },
    ),
  )
  .on(mediaMoveInFx.doneData, (store, category) => {
    // eslint-disable-next-line effector/no-getState
    const mediaIds = $selectedMediaIds.getState()

    return mediaIds.reduce(
      (result, id) => {
        const value = result[id]

        result[id] = {
          ...value,
          listStates: [...value.listStates, category.id],
        }

        return result
      },
      { ...store },
    )
  })
  .on(mediaMoveOutFx.doneData, (store, category) => {
    // eslint-disable-next-line effector/no-getState
    const mediaIds = $selectedMediaIds.getState()

    return mediaIds.reduce(
      (result, id) => {
        const value = result[id]

        result[id] = {
          ...value,
          listStates: value.listStates.filter(
            (categoryId) => categoryId !== category.id,
          ),
        }

        return result
      },
      { ...store },
    )
  })

export const $mediasToUse = combine(
  $mediaQueryParam,
  $searchedVaultMedias,
  $vaultMedias,
  (query, searched, vault) => {
    return query ? searched : vault
  },
)

export const $canSelectAllMedia = combine(
  $selectedMediaIds,
  $mediasToUse,
  $canLoadMoreVaultMediaPages,
  (selectedMediaIds, mediasToUse, hasMorePage) => {
    if (selectedMediaIds.length === MAX_MEDIA_SELECTION_LIMIT)
      return {
        value: false,
        message: `You can only select ${MAX_MEDIA_SELECTION_LIMIT} media at a time`,
      }

    const notSelectedMedias = mediasToUse.filter(
      (media) => !selectedMediaIds.includes(media.id),
    )

    if (notSelectedMedias.length === 0 && !hasMorePage)
      return {
        value: false,
        message: 'You have reached the end of the media list',
      }

    return {
      value: true,
      message: 'Select all media in this category',
    }
  },
)

export const selectAllMediaFx = attach({
  name: 'select all media',
  source: {
    selectedMediaIds: $selectedMediaIds,
    mediasToUse: $mediasToUse,
    hasMorePage: $canLoadMoreVaultMediaPages,
  },
  mapParams: (_: any, source) => source,
  effect: createEffect(
    async ({
      selectedMediaIds,
      mediasToUse,
      hasMorePage,
    }: {
      selectedMediaIds: number[]
      mediasToUse: VaultMediaItemDto[]
      hasMorePage: boolean
    }) => {
      const possibleSelectionCount =
        MAX_MEDIA_SELECTION_LIMIT - selectedMediaIds.length
      const notSelectedMedias = mediasToUse.filter(
        (media) => !selectedMediaIds.includes(media.id),
      )

      let mediaIdsToSelect = notSelectedMedias
        .slice(0, possibleSelectionCount)
        .map((media) => media.id)

      if (mediaIdsToSelect.length < possibleSelectionCount && hasMorePage) {
        const nextMedias = await getNextVaultMediasPageFx()

        if (nextMedias) {
          const moreIds = nextMedias.list
            .filter(
              (media) =>
                !selectedMediaIds.includes(media.id) &&
                !mediaIdsToSelect.includes(media.id),
            )
            .slice(0, possibleSelectionCount - mediaIdsToSelect.length)
            .map((media) => media.id)

          mediaIdsToSelect = [...mediaIdsToSelect, ...moreIds]
        }
      }

      return [...selectedMediaIds, ...mediaIdsToSelect]
    },
  ),
})

sample({
  clock: selectAllMediaFx.doneData,
  filter: (ids) => !!ids,
  target: setSelectedMediaIds,
})

// save to store
sample({
  clock: [getVaultMediasFx.doneData],
  fn: ({ list }) => list,
  target: updateMediaDataMap,
})

// save to store
sample({
  clock: loadSingleMediaFx.doneData,
  fn: (media) => [media],
  target: updateMediaDataMap,
})

// save to store
sample({
  clock: mediaSearchFx.doneData,
  fn: (media) => media,
  target: updateMediaDataMap,
})
